'use client'

import { useState, useRef } from 'react'
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop'
import { Button } from '@/components/ui/button'
import Image from 'next/image'
import 'react-image-crop/dist/ReactCrop.css'

interface ImageCropperProps {
  imageUrl: string
  onCropComplete: (croppedBlob: Blob) => void
  onCancel: () => void
  aspectRatio?: number
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

export function ImageCropper({
  imageUrl,
  onCropComplete,
  onCancel,
  aspectRatio = 1
}: ImageCropperProps) {
  const [crop, setCrop] = useState<Crop>()
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 })
  const [imgLoaded, setImgLoaded] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)

  const onImageLoad = () => {
    const image = imageRef.current
    if (image) {
      const { naturalWidth: width, naturalHeight: height } = image
      setImgDimensions({ width, height })
      setCrop(centerAspectCrop(width, height, aspectRatio))
      setImgLoaded(true)
    }
  }

  const getCroppedImg = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const image = imageRef.current
    if (!image || !crop) return

    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    // Calculate the actual dimensions based on the crop percentage
    const cropWidth = (crop.width / 100) * image.width * scaleX
    const cropHeight = (crop.height / 100) * image.height * scaleY
    const cropX = (crop.x / 100) * image.width * scaleX
    const cropY = (crop.y / 100) * image.height * scaleY

    canvas.width = cropWidth
    canvas.height = cropHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set the canvas to be high quality
    ctx.imageSmoothingQuality = 'high'

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    )

    // Convert the canvas to a Blob
    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob)
      }, 'image/jpeg', 1)
    })

    onCropComplete(blob)
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onCancel()
  }

  return (
    <div className="space-y-4">
      <ReactCrop
        crop={crop}
        onChange={(_, percentCrop) => setCrop(percentCrop)}
        aspect={aspectRatio}
        keepSelection
      >
        <Image
          ref={imageRef as React.RefObject<HTMLImageElement>}
          src={imageUrl}
          alt="Crop preview"
          width={imgDimensions.width || 400}
          height={imgDimensions.height || 400}
          className="max-w-full h-auto"
          onLoadingComplete={onImageLoad}
        />
      </ReactCrop>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={getCroppedImg} variant="atrium" disabled={!imgLoaded}>
          Crop Image
        </Button>
      </div>
    </div>
  )
} 